import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOrganizationDocumentActions } from '@/hooks/use-organization-document-actions';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '@/lib/queryClient';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Link2 } from 'lucide-react';
import { DocumentShareDialog } from '@/pages/project-details/components/DocumentShareDialog';
import { LogoLoader } from '@/components/ui/logo-loader';
import { IconButtonCancel, IconButtonSave } from '@/components/ui/icon-buttons';
import { useAlert } from '@/hooks/use-alert';
import { useDateFormat } from '@/utils/date-formatter';

interface OrganizationDocumentsTabProps {
  documents: any[];
  organizationId: string;
  triggerUpload?: boolean;
  onUploadTriggered?: () => void;
  searchTerm: string;
  onDeleteSelectedReady?: (removeSelected: () => void) => void;
  onEditSelectedReady?: (editSelected: () => Promise<void>) => void;
  onSelectionChange?: (count: number) => void;
}

export const OrganizationDocumentsTab: React.FC<OrganizationDocumentsTabProps> = ({
  documents,
  organizationId,
  triggerUpload,
  onUploadTriggered,
  searchTerm,
  onDeleteSelectedReady,
  onEditSelectedReady,
  onSelectionChange
}) => {
  const { t } = useTranslation(['pages.listen', 'common']);
  const { confirm, prompt } = useAlert();
  const { formatDate } = useDateFormat();
  const [connectionModalOpen, setConnectionModalOpen] = useState<string | null>(null);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [printLoadingId, setPrintLoadingId] = useState<string | null>(null);
  const [connectingDocumentId, setConnectingDocumentId] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);
  
  const {
    documentActionLoading,
    showDocumentUpload,
    uploadFile,
    isUploadingDocument,
    handleViewDocument,
    handleDownloadDocument,
    handleDeleteDocument,
    updateDocumentName,
    handleShareDocument,
    handleUploadClick,
    handleFileSelect,
    handleDocumentUpload,
    handleUploadCancel,
    connectToProjects,
    disconnectFromProject,
    shareDialogOpen,
    setShareDialogOpen,
    shareUrl,
    shareExpiresIn,
    shareDocumentName,
    connectToProjectsMutation,
    deleteDocumentMutation
  } = useOrganizationDocumentActions(organizationId);

  // Fetch projects for the organization
  const { data: projectsResponse } = useQuery({
    queryKey: ["/api/projects", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/projects?organizationId=${encodeURIComponent(organizationId)}` : "/api/projects";
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !!organizationId,
  });

  const projects = projectsResponse?.data || [];

  // Fetch connected projects for all documents using useQueries
  const connectedProjectsQueries = useQuery({
    queryKey: ['/api/documents', 'connected-projects', organizationId],
    queryFn: async () => {
      // Fetch connected projects for all documents
      const promises = documents.map(async (doc: any) => {
        try {
          const response = await apiRequest(`/api/documents/${doc.id}/projects`);
          if (response.ok) {
            const data = await response.json();
            return { documentId: doc.id, projects: data.data || [] };
          }
        } catch (error) {
          console.error(`Error fetching projects for document ${doc.id}:`, error);
        }
        return { documentId: doc.id, projects: [] };
      });
      const results = await Promise.all(promises);
      return new Map(results.map(r => [r.documentId, r.projects]));
    },
    enabled: documents.length > 0 && !!organizationId,
  });

  const connectedProjectsMap = connectedProjectsQueries.data || new Map<string, any[]>();
  
  const getConnectedProjectsForDocument = (documentId: string) => {
    return connectedProjectsMap.get(documentId) || [];
  };

  // Handle upload trigger from parent
  useEffect(() => {
    if (triggerUpload) {
      handleUploadClick();
      onUploadTriggered?.();
    }
  }, [triggerUpload, onUploadTriggered, handleUploadClick]);

  useEffect(() => {
    const currentIds = new Set(documents.map((doc: any) => doc.id));
    setSelectedDocumentIds((prev) => prev.filter((id) => currentIds.has(id)));
  }, [documents]);

  useEffect(() => {
    onSelectionChange?.(selectedDocumentIds.length);
  }, [onSelectionChange, selectedDocumentIds.length]);

  // Sort documents based on selected column and direction
  const filteredDocuments = useMemo(() => {
    if (!searchTerm.trim()) return documents;
    const searchLower = searchTerm.toLowerCase();
    return documents.filter((document: any) => {
      return (
        document.name?.toLowerCase().includes(searchLower) ||
        document.fileName?.toLowerCase().includes(searchLower)
      );
    });
  }, [documents, searchTerm]);

  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    if (!sortColumn) return 0;
    
    let aValue: any;
    let bValue: any;
    
    switch (sortColumn) {
      case "name":
        aValue = (a.name || '').toLowerCase();
        bValue = (b.name || '').toLowerCase();
        break;
      case "size":
        aValue = a.fileSize || 0;
        bValue = b.fileSize || 0;
        break;
      case "date":
        aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  // Handle column header click for sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleConnectClick = (document: any) => {
    const connected = getConnectedProjectsForDocument(document.id);
    setSelectedProjectIds(connected.map((p: any) => p.id));
    setConnectionModalOpen(document.id);
  };

  const handleConnectionSave = async (documentId: string) => {
    setConnectingDocumentId(documentId);
    try {
      await connectToProjects(documentId, selectedProjectIds);
    setConnectionModalOpen(null);
    setSelectedProjectIds([]);
    } finally {
      // Reset after mutation completes
      setTimeout(() => setConnectingDocumentId(null), 100);
    }
  };

  // Reset connecting state when mutation completes
  useEffect(() => {
    if (!connectToProjectsMutation.isPending && connectingDocumentId) {
      setConnectingDocumentId(null);
    }
  }, [connectToProjectsMutation.isPending, connectingDocumentId]);

  const deleteSelectedDocuments = useCallback(async () => {
    if (selectedDocumentIds.length === 0) return;
    const confirmed = await confirm({
      message: t('confirmations.deleteItems', { count: selectedDocumentIds.length }),
      variant: 'destructive',
      confirmText: t('buttons.delete'),
    });
    if (!confirmed) return;

    for (const documentId of selectedDocumentIds) {
      deleteDocumentMutation.mutate(documentId);
    }
    setSelectedDocumentIds([]);
  }, [confirm, deleteDocumentMutation, selectedDocumentIds, t]);

  useEffect(() => {
    if (!onDeleteSelectedReady) return;
    onDeleteSelectedReady(deleteSelectedDocuments);
  }, [deleteSelectedDocuments, onDeleteSelectedReady]);

  const editSelectedDocument = useCallback(async () => {
    if (selectedDocumentIds.length !== 1) return;

    const selectedDocumentId = selectedDocumentIds[0];
    const selectedDocument = documents.find((doc: any) => doc.id === selectedDocumentId);
    if (!selectedDocument) return;

    const updatedName = await prompt({
      title: t('documents.editModal.title'),
      message: t('documents.editModal.description'),
      defaultValue: selectedDocument.name || '',
      confirmText: t('buttons.save'),
      cancelText: t('buttons.cancel'),
    });

    if (updatedName === null) return;

    const trimmedName = updatedName.trim();
    if (!trimmedName || trimmedName === selectedDocument.name) return;

    await updateDocumentName(selectedDocument.id, trimmedName);
  }, [documents, prompt, selectedDocumentIds, t, updateDocumentName]);

  useEffect(() => {
    if (!onEditSelectedReady) return;
    onEditSelectedReady(editSelectedDocument);
  }, [editSelectedDocument, onEditSelectedReady]);

  const handleConnectionCancel = () => {
    setConnectionModalOpen(null);
    setSelectedProjectIds([]);
  };

  const handleProjectToggle = (projectId: string) => {
    setSelectedProjectIds(prev => 
      prev.includes(projectId) 
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  };

  const handleDisconnect = async (documentId: string, projectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    disconnectFromProject(documentId, projectId);
  };

  const inferMimeTypeFromName = (name: string): string | null => {
    const extension = name?.split('.').pop()?.toLowerCase();
    if (!extension) {
      return null;
    }

    switch (extension) {
      case 'pdf':
        return 'application/pdf';
      case 'png':
        return 'image/png';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'gif':
        return 'image/gif';
      case 'webp':
        return 'image/webp';
      default:
        return null;
    }
  };

  const handlePrintDocument = async (doc: any) => {
    setPrintLoadingId(doc.id);
    let temporaryObjectUrl: string | null = null;
    try {
      const response = await apiRequest(`/api/documents/${doc.id}/file`);
      if (!response.ok) {
        throw new Error('Failed to fetch document');
      }

      const responseContentType = response.headers.get('content-type')?.toLowerCase() ?? '';
      let sourceUrl: string | null = null;
      let fileName = doc.fileName || doc.name || 'Document';
      let fileType = (doc.fileType as string | undefined)?.toLowerCase() || inferMimeTypeFromName(fileName) || '';

      if (responseContentType.includes('application/json')) {
        const data = await response.json();
        if (!data?.fileUrl) {
          throw new Error('No file URL returned by API');
        }

        sourceUrl = data.fileUrl;
        if (typeof data.fileName === 'string' && data.fileName.trim().length > 0) {
          fileName = data.fileName;
        }
        if (!fileType) {
          fileType = inferMimeTypeFromName(fileName) || '';
        }
      } else {
        const blob = await response.blob();
        temporaryObjectUrl = URL.createObjectURL(blob);
        sourceUrl = temporaryObjectUrl;
        if (!fileType) {
          fileType = blob.type?.toLowerCase() || inferMimeTypeFromName(fileName) || '';
        }
      }

      if (!sourceUrl) {
        throw new Error('Failed to resolve document URL for printing');
      }

      const isPdf = fileType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');
      const isImage = fileType.startsWith('image/');

      // Open a new window for printing
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        throw new Error('Failed to open print window. Please allow popups for this site.');
      }

      if (isImage) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Print ${fileName}</title>
              <style>
                @media print {
                  body { margin: 0; padding: 0; }
                  img { max-width: 100%; height: auto; page-break-inside: avoid; }
                }
                @media screen {
                  body { margin: 0; padding: 20px; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
                  img { max-width: 100%; height: auto; }
                }
              </style>
            </head>
            <body>
              <img src="${sourceUrl}" alt="${fileName}" onload="setTimeout(() => { window.print(); }, 500)" />
            </body>
          </html>
        `);
        printWindow.document.close();
      } else if (isPdf) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Print ${fileName}</title>
              <style>
                body { margin: 0; padding: 0; overflow: hidden; }
                object { width: 100%; height: 100vh; border: none; }
              </style>
            </head>
            <body>
              <object data="${sourceUrl}" type="application/pdf" onload="setTimeout(() => { window.print(); }, 1500)">
                <p>PDF cannot be displayed. <a href="${sourceUrl}" target="_blank" rel="noopener noreferrer">Click here to download</a></p>
              </object>
            </body>
          </html>
        `);
        printWindow.document.close();

        // Also try to trigger print after a delay as fallback
        setTimeout(() => {
          try {
            printWindow.print();
          } catch (e) {
            console.error('Print error:', e);
          }
        }, 2000);
      } else {
        // For other file types, try to open directly
        printWindow.location.href = sourceUrl;

        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 1000);
        };
      }
    } catch (error) {
      console.error('Error printing document:', error);
    } finally {
      if (temporaryObjectUrl) {
        setTimeout(() => URL.revokeObjectURL(temporaryObjectUrl as string), 10000);
      }
      setPrintLoadingId(null);
    }
  };

  return (
    <div>
      
      {documents.length === 0 ? (
        <div className="card-container p-12">
          <div className="text-center">
            <div className="text-slate-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">{t('documents.empty.title')}</h3>
            <p className="text-slate-600 mb-4">{t('documents.empty.description')}</p>
            <button 
              className="button-primary"
              onClick={handleUploadClick}
            >
              {t('documents.upload')}
            </button>
          </div>
        </div>
      ) : (
        <div className="h-[50vh] flex flex-col">
          <div className="bg-white shadow-none rounded-none overflow-hidden flex-1 flex flex-col">
            <div className="w-full flex-1 flex flex-col overflow-hidden">
              <div className="overflow-x-auto overflow-y-auto flex-1">
                <table className="min-w-full listen-table">
                  <thead className="sticky top-0 z-10">
                    <tr className="table-header-row">
                      <th className="table-header-cell w-10">
                        <Checkbox
                          checked={
                            selectedDocumentIds.length > 0 &&
                            selectedDocumentIds.length === filteredDocuments.length
                              ? true
                              : selectedDocumentIds.length > 0
                                ? "indeterminate"
                                : false
                          }
                          onCheckedChange={() => {
                            if (selectedDocumentIds.length === filteredDocuments.length) {
                              setSelectedDocumentIds([]);
                              return;
                            }
                            setSelectedDocumentIds(filteredDocuments.map((document: any) => document.id));
                          }}
                          aria-label={t('table.headers.selectAll')}
                        />
                      </th>
                      <th className="table-header-cell">
                        <button 
                          onClick={() => handleSort('name')}
                          className="flex items-center gap-1 hover:text-slate-900 w-full text-left"
                        >
                          {t('documents.table.name')}
                          {sortColumn === "name" && <span className="text-[10px]">{sortDirection === "asc" ? "↓" : "↑"}</span>}
                        </button>
                      </th>
                      <th className="table-header-cell">
                        <button 
                          onClick={() => handleSort('size')}
                          className="flex items-center gap-1 hover:text-slate-900 w-full text-left"
                        >
                          {t('documents.table.size')}
                          {sortColumn === "size" && <span className="text-[10px]">{sortDirection === "asc" ? "↓" : "↑"}</span>}
                        </button>
                      </th>
                      <th className="table-header-cell">
                        <button 
                          onClick={() => handleSort('date')}
                          className="flex items-center gap-1 hover:text-slate-900 w-full text-left"
                        >
                          {t('documents.table.date')}
                          {sortColumn === "date" && <span className="text-[10px]">{sortDirection === "asc" ? "↓" : "↑"}</span>}
                        </button>
                      </th>
                      <th className="table-header-cell">{t('documents.connectedProjects')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDocuments.map((document: any) => (
                      <tr 
                        key={document.id} 
                        className="table-body-row cursor-pointer"
                        onClick={() => handleViewDocument(document)}
                      >
                        <td className="table-body-cell">
                          <Checkbox
                            checked={selectedDocumentIds.includes(document.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedDocumentIds((prev) => [...prev, document.id]);
                              } else {
                                setSelectedDocumentIds((prev) => prev.filter((id) => id !== document.id));
                              }
                            }}
                            onClick={(event) => event.stopPropagation()}
                            aria-label={t('table.headers.selectRow')}
                          />
                        </td>
                        <td className="table-body-cell">
                          <div>{document.name}</div>
                        </td>
                        <td className="table-body-cell whitespace-nowrap">
                          {(document.fileSize / 1024).toFixed(1)} KB
                        </td>
                        <td className="table-body-cell whitespace-nowrap">
                          {formatDate(document.createdAt)}
                        </td>
                        <td className="table-body-cell" onClick={(e) => e.stopPropagation()}>
                          <div className="flex flex-wrap gap-1 items-center">
                            {(() => {
                              const connectedProjects = getConnectedProjectsForDocument(document.id);
                              const count = connectedProjects.length;
                              return count > 0 ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                                  {t('documents.connectedProjectsCount', { count, defaultValue: `Connected projects (${count})` })}
                                </span>
                              ) : null;
                            })()}
                            <button
                              onClick={() => handleConnectClick(document)}
                              disabled={connectingDocumentId === document.id || connectToProjectsMutation.isPending}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs text-blue-600 hover:bg-blue-50 border border-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={connectingDocumentId === document.id ? t('documents.loading') : t('documents.connectionModal.title')}
                            >
                              {connectingDocumentId === document.id ? (
                                <LogoLoader size="sm" />
                              ) : (
                                <>
                              <Link2 className="h-3 w-3" />
                                  <span>{t('documents.connectToProjects')}</span>
                                </>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      <Dialog open={showDocumentUpload} onOpenChange={(open) => !open && handleUploadCancel()}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-hidden flex flex-col p-0 bg-[#E8E8E8] rounded-2xl" hideClose>
          {/* Custom Header with Cancel Button */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-300 bg-[#E8E8E8] rounded-t-2xl">
            <h2 
              className="text-lg font-semibold text-black"
              style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}
            >
              {t('documents.uploadModal.title')}
            </h2>
            <div className="flex items-center gap-2">
              <IconButtonCancel
                onClick={handleUploadCancel}
                disabled={isUploadingDocument}
                size={30}
                title={t('cancel', { ns: 'common' })}
              />
              <IconButtonSave
                onClick={handleDocumentUpload}
                disabled={isUploadingDocument || !uploadFile}
                size={30}
                title={t('save', { ns: 'common' })}
              />
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Upload Section */}
            <fieldset className="border border-gray-300 rounded-lg p-4 bg-white">
              <legend 
                className="px-2 text-sm font-medium text-black"
                style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}
              >
                {t('documents.uploadModal.selectFile')}
              </legend>
              <div className="flex flex-col items-center gap-3">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="org-document-file-input"
                />
                <Button 
                  variant="outline" 
                  onClick={() => document.getElementById('org-document-file-input')?.click()}
                  disabled={isUploadingDocument}
                  className="min-w-[150px]"
                >
                  {isUploadingDocument ? <LogoLoader size="sm" /> : t('documents.uploadModal.browseFiles', 'Browse Files...')}
                </Button>
                {uploadFile && (
                  <div className="w-full p-2 bg-gray-50 rounded text-sm text-center">
                    <strong>{t('documents.uploadModal.selected')}:</strong> {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
                  </div>
                )}
              </div>
            </fieldset>
          </div>
        </DialogContent>
      </Dialog>

      {/* Connection Management Modal */}
      {connectionModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card-container p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">
                {t('documents.connectionModal.title')}
              </h2>
              <button
                onClick={handleConnectionCancel}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                {t('documents.connectionModal.selectProjects')}
              </p>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {projects.map((project: any) => (
                  <div key={project.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`project-${project.id}`}
                      checked={selectedProjectIds.includes(project.id)}
                      onCheckedChange={() => handleProjectToggle(project.id)}
                    />
                    <label
                      htmlFor={`project-${project.id}`}
                      className="text-sm font-medium cursor-pointer flex-1"
                    >
                      {project.name}
                    </label>
                  </div>
                ))}
                {projects.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">
                    {t('documents.connectionModal.noProjects')}
                  </p>
                )}
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={handleConnectionCancel}
                  className="button-outline"
                >
                  {t('cancel', { ns: 'common' })}
                </button>
                <button
                  onClick={() => handleConnectionSave(connectionModalOpen)}
                  className="button-primary"
                >
                  {t('save', { ns: 'common' })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Dialog */}
      <DocumentShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        shareUrl={shareUrl}
        expiresIn={shareExpiresIn}
        documentName={shareDocumentName}
      />
    </div>
  );
};
